import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useMutation, useQueryClient } from "react-query";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useRecoilValue, useSetRecoilState } from "recoil";
import {
  editContentNumState,
  isDeleteLoadingState,
  isDetailState,
  isEditState,
  isProfilePageState,
  isThroughMainState,
  popState,
  writingTypeState
} from "recoil/contentsAtom";
import { redirectURL } from "utils/CommonFunc";
import { DeleteApi } from "api/postListApi";
import "components/community/common/isMyarticle/IsMyArticle.scoped.scss";
import {
  headersState,
  isLoginStateSelector,
  isProfileOwnerState,
  userInfoState
} from "recoil/userInfoAtom";
import { faCaretRight, faEllipsis } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { BASE_PATH } from "api/commonApi";
import { useLoginCheck } from "hooks/useLoginCheck";

interface IProps {
  idx: number;
  nickname: {
    to: string;
    from: string;
  };
  isWriter: boolean;
  serialNum: {
    to: number;
    from: number;
  };
  profilekey: string;
}

// profile
// 토글이 보이는 조건
// 1. 내가 나에게 / 작성자 나 / 대상 나
// 2. 타인이 나에게 / 작성자 타인 / 대상 나
// 3. 내가 타인에게 / 작성자 나 / 대상 타인

// user_Serial_from === userSerial -> 작성자가 누구인지 판단 / 작성자일 경우 수정, 삭제 모두 가능

// 위가 true이고 user_Serial_to === userSerial 이면 내가 나에게 쓴 글
// 위가 false이고 user_Serial_to !== userSerial 타인이 나에게
// 위가 true이고 user_Serial_to !== userSerial 이면 내가 타인에게

// isWriter로 수정 삭제 조건 넣기

function IsMyArticle(props: IProps) {
  const { t } = useTranslation();
  const { service_code, title, profilekey } = useParams();
  const isLoginState = useRecoilValue(isLoginStateSelector);
  const userInfo = useRecoilValue(userInfoState);
  const headers = useRecoilValue(headersState);
  const isDetail = useRecoilValue(isDetailState);
  const setPopOpen = useSetRecoilState(popState);
  const setWritingType = useSetRecoilState(writingTypeState);
  const setIsEdit = useSetRecoilState(isEditState);
  const setEditContentNum = useSetRecoilState(editContentNumState);
  const setIsDeleteLoading = useSetRecoilState(isDeleteLoadingState);
  const [editingToggle, setEditingToggle] = useState<any>({});
  const navigate = useNavigate();
  const isThroughMain = useRecoilValue(isThroughMainState);
  const isProfilePage = useRecoilValue(isProfilePageState);
  const isProfileOwner = useRecoilValue(isProfileOwnerState);
  const isNotLogin = useLoginCheck();
  const type = isProfilePage ? "profile_board_list" : "page_board_list";

  const handleArticleEditor = (idx: number) => {
    setEditingToggle({
      [idx]: !editingToggle[idx]
    });

    if (!isLoginState.isLogIn) {
      isNotLogin();
    }
  };

  const useDeleteMutation = () => {
    const queryClient = useQueryClient();
    return useMutation(DeleteApi, {
      onSuccess: (api) => {
        queryClient.invalidateQueries([type]);
        setIsDeleteLoading(false);
      },
      onError: (error: any) => {
        alert(t(error.response.data.message));
        setIsDeleteLoading(false);
      }
    });
  };

  const { mutate: deleteMutate } = useDeleteMutation();

  const deleteBoard = (idx: number) => {
    const api = isProfilePage
      ? `${BASE_PATH}/api/vfunlounge/profile/post?board_idx=${idx}`
      : `${BASE_PATH}/api/vfunlounge/posts/${service_code}/${
          title === undefined ? "all" : title
        }/${idx}`;

    if (window.confirm(t("are_you_sure_you_want_to_delete_the_post"))) {
      const props = { service_code, title, idx, headers, api };
      deleteMutate(props);
      setIsDeleteLoading(true);
      setEditingToggle({});

      // 1. 리스트일 때 삭제 : isThroughMain === true && isDetail === false => url 이동 없음
      // 2. 리스트를 통한 디테일에서 삭제 : isThroughMain === true && isDetail === true => 리스트로 돌아가야 함 , navigate(-1)
      // 3. 복사된 디테일에서 삭제 : isThroughMain === false && isDetail === true => redirect 필요

      if (isDetail) {
        // 2번과 3번
        if (isThroughMain) {
          // 2번
          navigate(-1);
        } else {
          // 3번
          redirectURL(
            isProfilePage
              ? `${window.location.origin}/profile/${profilekey}/post`
              : `${window.location.origin}/page/${service_code}`,
            "_self",
            isLoginState.isLauncher
          );
        }
      }
    }
  };

  const editBoard = (idx: number) => {
    setIsEdit(true);
    setWritingType("content");
    setPopOpen(true);
    setEditContentNum(idx);
  };

  const onClickNickname = (profilekey: string) => {
    if (profilekey === "" || profilekey === null) {
      alert("존재하지 않은 프로필 유저입니다.");
    } else {
      navigate(`/profile/${profilekey}/post`);
    }
  };

  return (
    <div className="info-writing" id="IsMyArticle">
      <span className="nickname clearfix">
        {/* game page nickname 요청하기 */}
        <span
          onClick={() => onClickNickname(props.profilekey || "")}
          className="name"
        >
          {props.nickname.from}
        </span>

        {isProfilePage ? (
          props.serialNum?.to !== props.serialNum?.from ? (
            <>
              <FontAwesomeIcon icon={faCaretRight} />
              {props.nickname.to}
            </>
          ) : null
        ) : null}

        {isLoginState.isLogIn &&
          (props.isWriter || isProfileOwner ? (
            <Link
              to="#"
              className="btns icon-dots"
              onClick={(e) => handleArticleEditor(props.idx)}
            >
              <FontAwesomeIcon icon={faEllipsis} />
            </Link>
          ) : null)}
      </span>

      {/* 오너이거나 작성자에게 보여짐 */}
      {isLoginState.isLogIn &&
        (props.serialNum?.from === userInfo.userSerial || isProfileOwner ? (
          editingToggle[props.idx] ? (
            <ul className="btns editing">
              {/* 수정은 작성자만 가능 */}
              {props.isWriter && (
                <li className="edit">
                  <Link to="#" onClick={() => editBoard(props.idx)}>
                    <span className="icon edit" />
                    {t("edit")}
                  </Link>
                </li>
              )}

              <li className="delete">
                <Link to="#" onClick={() => deleteBoard(props.idx)}>
                  <span className="icon delete" />
                  {t("delete")}
                </Link>
              </li>
            </ul>
          ) : null
        ) : null)}
    </div>
  );
}

export default IsMyArticle;
